/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectLegalStandard.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import SelectLegalRequirementCard from "./SelectLegalRequirementCard";
import { legalRequirements } from "../../../../redux/actions";
import { useSelector } from "react-redux";

function SelectLegalStandard({
  componentType,
  setComponentType,
  setSelectedLegal,
  displayPageMap,
  displayPage,
  setDisplayPage,
  selectedLegal,
  savedLinkages,
  disableLrRadioButton,
  disabledLegalRequirement,
  shouldShowLegalRequirementLinkedInfo = () => false,
  selectedStandard,
  setSelectedStandard,
  selectedChapter,
  setSelectedChapter,
  selectedLegalStandard,
  setSelectedLegalStandard,
  legalChapterList,
  setLegalChapterList,
  aiRecommendedEntities = [],
}) {
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails
  );

  const [legalStandardList, setLegalStandardList] = useState([]);
  const [legalStandardPage, setLegalStandardPage] = useState(1);
  const [legalStandardRowsPerPage, setLegalStandardRowsPerPage] = useState(25);
  const [legalChapterPage, setLegalChapterPage] = useState(1);
  const [legalChapterRowsPerPage] = useState(25);
  const [legalStandardCount, setLegalStandardCount] = useState(0);
  const [selectedLegalChapter, setSelectedLegalChapter] = useState(null);
  const [legalChapterCount, setLegalChapterCount] = useState(0);
  const [legalRequirementPage, setLegalRequirementPage] = useState(1);
  const [legalRequirementRowsPerPage] =
    useState(25);
  const [legalRequirementList, setLegalRequirementList] = useState([]);

  const [legalRequirementCount, setLegalRequirementCount] = useState(0);

  const getChaptersForLs = async (item) => {
    const modifiedLegalChapters = item.chapters.map((chapter) => {
      return {
        chapter: chapter,
      };
    });
    setLegalChapterList(modifiedLegalChapters);
    setDisplayPage(displayPageMap.lc);
  };

  const aiSuggestedLegalIds = useMemo(() => {
    return new Set(
      aiRecommendedEntities
        .filter((e) => e.entityType === "LEGAL_REQUIREMENT")
        .map((e) => Number(e.entityId))
    );
  }, [aiRecommendedEntities]);

  return (
    <SelectLegalRequirementCard
      savedLinkages={savedLinkages}
      disableLrRadioButton={disableLrRadioButton}
      displayPage={displayPage}
      displayPageMap={displayPageMap}
      componentType={componentType}
      setComponentType={setComponentType}
      lsData={legalStandardList}
      lcData={legalChapterList}
      selectedLegalStandard={selectedLegalStandard}
      getChangedLsItem={(item) => {
        setSelectedStandard(item.standard_number);
        setSelectedLegalStandard(item);
        getChaptersForLs(item);
      }}
      lsApiAction={legalRequirements.get_legal_standard_details_new}
      searchKey={"standard_number"}
      placeholder={"Select Legal Standard"}
      getDataOnSearch={(data, resp) => {
        setLegalStandardList(data || []);
        setLegalStandardCount(resp?.total_count);
      }}
      lcParams={{
        ...(selectedProjectDetails?.delivery_market?.length && {
          markets: selectedProjectDetails?.delivery_market?.join("_"),
        }),
        page_number: legalChapterPage,
        page_size: legalChapterRowsPerPage,
        module_type: componentType,
      }}
      lsParams={{
        ...(selectedProjectDetails?.delivery_market?.length && {
          markets: selectedProjectDetails?.delivery_market,
        }),
        page_number: legalStandardPage,
        page_size: legalStandardRowsPerPage,
        ...(componentType !== "all" && {
          module_type: componentType,
          effective_from: selectedVariant?.production_year,
        }),
      }}
      selectedLegalChapter={selectedLegalChapter}
      getChangedLcItem={(item) => {
        setSelectedChapter(item.chapter);
        setSelectedLegalChapter(item.chapter);
        setDisplayPage(displayPageMap.lr);
      }}
      legalStandardRowsPerPage={legalStandardRowsPerPage}
      legalStandardPage={legalStandardPage}
      setLegalStandardRowsPerPage={setLegalStandardRowsPerPage}
      setLegalStandardPage={setLegalStandardPage}
      legalChapterCount={legalChapterCount}
      setLegalChapterCount={setLegalChapterCount}
      legalChapterPage={legalChapterPage}
      setLegalChapterPage={setLegalChapterPage}
      legalChapterRowsPerPage={legalChapterRowsPerPage}
      legalStandardCount={legalStandardCount}
      legalRequirementsParams={{
        ls_id: selectedLegalStandard?.ls_id,
        chapter: selectedLegalChapter,
        page_number: legalRequirementPage,
        page_size: legalRequirementRowsPerPage,
      }}
      legalRequirementApi={legalRequirements.get_legal_requirements_details_new}
      getLrDataOnSearch={(data, resp) => {
        setLegalRequirementList(data);
        setLegalRequirementCount(resp?.total_count);
      }}
      lrSearchKey={"req_number"}
      lrPlaceholder={"Select Legal Requirement"}
      lrPage={legalRequirementPage}
      lrRowsPerPage={legalRequirementRowsPerPage}
      setLrPage={setLegalRequirementPage}
      totalLrCount={legalRequirementCount}
      legalRequirementList={legalRequirementList}
      disabledLegalRequirement={disabledLegalRequirement || []}
      shouldShowLegalRequirementLinkedInfo={shouldShowLegalRequirementLinkedInfo}
      selectedLegal={selectedLegal}
      setSelectedLegal={setSelectedLegal}
      getSelectedLrItem={(items) => {
        setSelectedLegal(items);
      }}
      aiSuggestedLegalIds={aiSuggestedLegalIds}
    />
  );
}

SelectLegalStandard.propTypes = {
  componentType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setComponentType: PropTypes.func,
  setSelectedLegal: PropTypes.func,
  displayPageMap: PropTypes.shape({
    ls: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lc: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  displayPage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setDisplayPage: PropTypes.func,
  selectedLegal: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  savedLinkages: PropTypes.array,
  disableLrRadioButton: PropTypes.bool,
  disabledLegalRequirement: PropTypes.array,
  shouldShowLegalRequirementLinkedInfo: PropTypes.func,
  selectedStandard: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedStandard: PropTypes.func,
  selectedChapter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedChapter: PropTypes.func,
  selectedLegalStandard: PropTypes.object,
  setSelectedLegalStandard: PropTypes.func,
  legalChapterList: PropTypes.array,
  setLegalChapterList: PropTypes.func,
  aiRecommendedEntities: PropTypes.array,
};

export default SelectLegalStandard;
